import { Component, OnInit } from '@angular/core';
import { BaseComponent, UtilsArray, UtilsMisc, UtilsString } from '@pit/core';
import { IDtoDepot, IDtoSetting, IDtoUser, IDtoUserRole, UserState } from '@pit/frontend-model';
import { DynamicDialogConfig, DynamicDialogRef } from 'primeng/dynamicdialog';
import { Observable } from 'rxjs';
import { PinItTrackFrontendServicesAccessor } from '../../module.core/accessor/implementations/pin.it.track.frontend.services.accessor';

@Component({
    selector: 'pin-it-track-frontend-user-dialog',
    templateUrl: './pin.it.track.frontend.user.dialog.component.html',
    styleUrls: ['./pin.it.track.frontend.user.dialog.component.scss']
})
export class PinItTrackFrontendUserDialogComponent extends BaseComponent {

    private _isActive: boolean = true;
    private _isAdmin: boolean = false;
    private _isAdminSuper: boolean = false;
    private _isDispatcher: boolean = false;
    private _isMobileUser: boolean = false;
    private _userNumbersValidRegex: RegExp | null = null;
    private _userNumbersValidExamples: string = '';

    constructor(private servicesAccessor: PinItTrackFrontendServicesAccessor, private dialogRef: DynamicDialogRef, private dialogConfig: DynamicDialogConfig) {
        super(servicesAccessor.coreServicesAccessor.applicationService);

        this.user = this.dialogConfig.data.user;
        this._isActive = (this.user.userState === UserState.Avtive);
        this._isAdmin = this.user.isAdmin;
        this._isAdminSuper = this.user.isAdminSuper;
        this._isDispatcher = this.user.isDispatcher;
        this._isMobileUser = this.user.isMobileUser;

        if (this.user.id === null) {
            this.user.passwordResetRequested = true;
        }
    }

    public get canSave(): boolean {
        return !this.isGeneralTabInvalid && !this.isRightsTabInvalid;
    }

    public get isUserNameInvalid(): boolean {
        return UtilsString.stringIsNullOrEmpty(this.user?.userName) || (this.user?.userName?.length < 2);
    }

    public get isUserNumberInvalid(): boolean {
        if (UtilsString.stringIsNullOrEmpty(this.user?.userNumber)) {
            return true;
        }

        if (this._userNumbersValidRegex === null) {
            return true;
        }

        return !this._userNumbersValidRegex.test(this.user.userNumber);
    }

    public get isLastNameInvalid(): boolean {
        return UtilsString.stringIsNullOrEmpty(this.user?.lastName) || (this.user?.lastName?.length < 2);
    }

    public get isRoleInvalid(): boolean {
        return !this.isAdmin && !this.isDispatcher;
    }

    public get isPasswordInvalid(): boolean {
        // Password is only invalid if:
        // 1. It's a new user OR password reset is requested
        // 2. AND it's not an LDAP user
        // 3. AND the password is empty
        return (this.user?.id === null || this.user?.passwordResetRequested === true) &&
            (this.user?.mandator?.useLDAP !== true) &&
            UtilsString.stringIsNullOrEmpty(this.user?.password);
    }

    public get isGeneralTabInvalid(): boolean {
        return this.isUserNameInvalid || this.isUserNumberInvalid || this.isLastNameInvalid;
    }

    public get isRightsTabInvalid(): boolean {
        return this.isRoleInvalid || this.isPasswordInvalid;
    }

    public currentUser: IDtoUser = this.servicesAccessor.currentUser;

    public depots$: Observable<IDtoDepot[]> = this.servicesAccessor.entityStoreFacadesAccessor.depotStoreFacade.items$;

    public get isActive(): boolean {
        return this._isActive;
    }

    public set isActive(value: boolean) {
        if (value !== this.isActive) {
            this._isActive = value;

            if (this.isActive === true) {
                this.user.userState = UserState.Avtive;
            } else {
                this.user.userState = UserState.Deactive;
            }
        }
    }

    public get isAdmin(): boolean {
        return this._isAdmin;
    }

    public set isAdmin(value: boolean) {
        if (value !== this.isAdmin) {
            this._isAdmin = value;

            if (this.isAdmin === true) {
                if (this.user.isAdmin === false) {
                    const adminRole = this.availableUserRoles.find(x => (x.code === 'ADMIN'));
                    if (adminRole) {
                        this.user.userRoles.push(adminRole);
                    }
                }
            } else {
                if (this.user.isAdmin === true) {
                    const userRole: IDtoUserRole = this.user.userRoles.find(x => (x.code === 'ADMIN'));
                    if (userRole) {
                        UtilsArray.removeItem(this.user.userRoles, userRole);
                    }
                }
            }
        }
    }

    public get isAdminSuper(): boolean {
        return this._isAdminSuper;
    }

    public set isAdminSuper(value: boolean) {
        if (value !== this.isAdminSuper) {
            this._isAdminSuper = value;

            if (this.isAdminSuper === true) {
                if (this.user.isAdminSuper === false) {
                    const adminSuperRole = this.availableUserRoles.find(x => (x.code === 'ADMINSUPER'));
                    if (adminSuperRole) {
                        this.user.userRoles.push(adminSuperRole);
                    }
                }
            } else {
                if (this.user.isAdminSuper === true) {
                    const userRole: IDtoUserRole = this.user.userRoles.find(x => (x.code === 'ADMINSUPER'));
                    if (userRole) {
                        UtilsArray.removeItem(this.user.userRoles, userRole);
                    }
                }
            }
        }
    }

    public get isDispatcher(): boolean {
        return this._isDispatcher;
    }

    public set isDispatcher(value: boolean) {
        if (value !== this.isDispatcher) {
            this._isDispatcher = value;

            if (this.isDispatcher === true) {
                if (this.user.isDispatcher === false) {
                    const dispatcherRole = this.availableUserRoles.find(x => (x.code === 'DISPATCHER'));
                    if (dispatcherRole) {
                        this.user.userRoles.push(dispatcherRole);
                    }
                }
            } else {
                if (this.user.isDispatcher === true) {
                    const userRole: IDtoUserRole = this.user.userRoles.find(x => (x.code === 'DISPATCHER'));
                    if (userRole) {
                        UtilsArray.removeItem(this.user.userRoles, userRole);
                    }
                }
            }
        }
    }

    public get isMobileUser(): boolean {
        return this._isMobileUser;
    }

    public set isMobileUser(value: boolean) {
        if (value !== this.isMobileUser) {
            this._isMobileUser = value;

            if (this.isMobileUser === true) {
                if (this.user.isMobileUser === false) {
                    const mobileRole = this.availableUserRoles.find(x => (x.code === 'MOBILE'));
                    if (mobileRole) {
                        this.user.userRoles.push(mobileRole);
                    }
                }
            } else {
                if (this.user.isMobileUser === true) {
                    const userRole: IDtoUserRole = this.user.userRoles.find(x => (x.code === 'MOBILE'));
                    if (userRole) {
                        UtilsArray.removeItem(this.user.userRoles, userRole);
                    }
                }
            }
        }
    }

    public user: IDtoUser = {};

    public get userNumberValidationMessage(): string {
        return `Benutzernummer muss einem gültigen Format entsprechen. Beispiele: ${this._userNumbersValidExamples}`;
    }

    public async cancelAsync(): Promise<void> {
        this.dialogRef.close();
    }

    public async saveAsync(): Promise<void> {
        let success = false;
        try {
            this.servicesAccessor.eventService.useMethod('busy.on');
            if (this.user.id === null) {
                this.user.passwordResetRequested = true;
            }
            const user: IDtoUser = await this.servicesAccessor.entityStoreFacadesAccessor.userStoreFacade.saveItemAsync(this.user);
            if (user.id === this.servicesAccessor.currentUser.id) {
                this.servicesAccessor.currentUser = user;
            }
            success = true;
        } catch (error: any) {
            // Check if this is a duplicate username error (HTTP 409 Conflict)
            if (error && error.status === 409) {
                // Show specific confirmation dialog for duplicate username
                this.servicesAccessor.eventService.sendEvent('confirmation', {
                    message: 'Ein Benutzer mit dieser Benutzerkennung existiert bereits. Bitte wählen Sie eine andere Benutzerkennung.',
                    header: 'Benutzername bereits vorhanden',
                    icon: 'pi pi-exclamation-triangle',
                    acceptLabel: 'OK',
                    rejectLabel: null, // Hide cancel button
                    callback: {
                        method: async (confirmed: boolean) => {
                            // No action needed, just acknowledgment
                        },
                        data: null
                    }
                });
                return; // Keep dialog open, don't close
            } else {
                // For other errors, show error
                this.servicesAccessor.eventService.useMethod('error.show', error);
            }
        } finally {
            this.servicesAccessor.eventService.useMethod('busy.off');
        }

        // Only close dialog on success
        if (success) {
            this.dialogRef.close();
        }
    }

    public getUserHasDepot(depot: IDtoDepot): boolean {
        if (!this.user?.depots) {
            return false;
        }
        return (UtilsMisc.isNullOrUndefined(this.user.depots.find(x => (x.id === depot.id))) === false);
    }

    public onDepotCheckChanged(event: any, depot: IDtoDepot): void {
        if (!this.user?.depots) {
            this.user.depots = [];
        }

        // Create a new array to avoid extensibility errors
        let newDepots = [...this.user.depots];

        if (event.checked === true) {
            if (this.getUserHasDepot(depot) === false) {
                newDepots.push(depot);
            }
        } else {
            const userDepot: IDtoDepot = newDepots.find(x => (x.id === depot.id));
            if (UtilsMisc.isNullOrUndefined(userDepot) === false) {
                newDepots = newDepots.filter(x => x.id !== depot.id);
            }
        }

        // Replace the entire array
        this.user.depots = newDepots;
    }

    protected override async doOnAfterViewInitAsync(): Promise<void> {
        await super.doOnAfterViewInitAsync();

        try {
            this.servicesAccessor.eventService.useMethod('busy.on');
            await this.servicesAccessor.entityStoreFacadesAccessor.depotStoreFacade.loadDepotsForMandatorAsync(this.servicesAccessor.currentUser.mandator.id);
            await this.servicesAccessor.entityStoreFacadesAccessor.userRoleStoreFacade.loadItemsAsync();
            this.availableUserRoles = (await this.servicesAccessor.entityStoreFacadesAccessor.userRoleStoreFacade.getAllItemsAsync()) ?? [];
            await this.loadUserNumbersValidSettingAsync();

            // For new users, default to dispatcher role if no roles are assigned
            if (this.user.id === null && !this._isAdmin && !this._isAdminSuper && !this._isDispatcher) {
                this.isDispatcher = true;
            }
        } catch (error) {
            this.servicesAccessor.eventService.useMethod('error.log', error);
        } finally {
            this.servicesAccessor.eventService.useMethod('busy.off');
        }
    }

    private availableUserRoles: IDtoUserRole[] = [];

    private async loadUserNumbersValidSettingAsync(): Promise<void> {
        try {
            const mandatorId = this.currentUser?.mandator?.id;
            if (mandatorId) {
                const settings = await this.servicesAccessor.dataServicesAccessor.settingDataService.getSettingsForMandatorAsync(mandatorId);
                const userNamesValidSetting = settings.find(s => s.paramKey === 'userNamesValid');

                if (userNamesValidSetting && userNamesValidSetting.paramValue) {
                    this._userNumbersValidRegex = new RegExp(userNamesValidSetting.paramValue);
                    this._userNumbersValidExamples = this.generateExamplesFromRegex(userNamesValidSetting.paramValue);
                }
            }
        } catch (error) {
            console.error('Error loading userNamesValid setting:', error);
            // Fallback to default regex if loading fails
            this._userNumbersValidRegex = /(^003[0-9]{4}$)|(^091[0-9]{5})|(^0091[0-9]{3})/;
            this._userNumbersValidExamples = '0031234, 09112345, 0091123';
        }
    }

    private generateExamplesFromRegex(regexPattern: string): string {
        try {
            // Simple static examples based on common patterns
            if (regexPattern.includes('003[0-9]{4}')) {
                return '0031234, 09112345, 0091123';
            }
            if (regexPattern.includes('[0-9]')) {
                return '0031234, 09112345, 0091123';
            }
            return '0031234, 09112345, 0091123';
        } catch (error) {
            console.error('Error generating examples from regex:', error);
            return '0031234, 09112345, 0091123';
        }
    }


}
